import { DEFAULT_DESIGN_ID } from '../designs/catalog';
import { uid } from '../lib/id';
import { DATA_VERSION, type Line, type Note, type NoteStyle, type Settings } from '../storage/schemas';

export const DEFAULT_FONT_SCALE = 7;

export function newLine(text = '', kind: Line['kind'] = 'text'): Line {
  return { id: uid('l'), text, kind, checked: false };
}

export function defaultStyle(settings?: Partial<Settings>): NoteStyle {
  return {
    fontScale: DEFAULT_FONT_SCALE,
    autoSize: settings?.autoSize ?? true,
    align: 'block',
    vAlign: 'center',
    lineHeight: 1.28,
    compact: settings?.compactDefault ?? false,
    watermark: settings?.watermark ?? true,
    completed: settings?.completedBehavior ?? 'strike',
  };
}

export function createNote(init: Partial<Note> = {}, settings?: Partial<Settings>): Note {
  const now = Date.now();
  return {
    id: uid('n'),
    version: DATA_VERSION,
    title: '',
    lines: [newLine()],
    images: [],
    strokes: [],
    designId: settings?.defaultDesignId ?? DEFAULT_DESIGN_ID,
    style: defaultStyle(settings),
    tags: [],
    pinned: false,
    favorite: false,
    archived: false,
    deletedAt: null,
    order: -now,
    createdAt: now,
    updatedAt: now,
    rev: 0,
    ...init,
  };
}

export function noteText(note: Pick<Note, 'lines'>): string {
  return note.lines.map((l) => (l.kind === 'check' ? `${l.checked ? '[x]' : '[ ]'} ${l.text}` : l.text)).join('\n');
}

export function plainText(note: Pick<Note, 'lines'>): string {
  return note.lines.map((l) => l.text).join('\n');
}

export function charCount(note: Pick<Note, 'lines'>): number {
  // Count user-perceived characters (grapheme clusters) so emoji and CJK count as one.
  const text = note.lines.map((l) => l.text).join('');
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    let n = 0;
    for (const _ of new Intl.Segmenter().segment(text)) n++;
    return n;
  }
  return [...text].length;
}

export function displayTitle(note: Pick<Note, 'title' | 'lines' | 'images' | 'strokes'>): string {
  if (note.title.trim()) return note.title.trim();
  const first = note.lines.find((l) => l.text.trim());
  if (first) return first.text.trim().slice(0, 80);
  if (note.images.length) return 'Photo note';
  if (note.strokes.length) return 'Drawing';
  return 'Empty note';
}

export function isEmptyNote(note: Pick<Note, 'lines' | 'images' | 'strokes'>): boolean {
  return note.images.length === 0 && note.strokes.length === 0 && note.lines.every((l) => !l.text.trim());
}

export function checklistProgress(note: Pick<Note, 'lines'>): { done: number; total: number } {
  const checks = note.lines.filter((l) => l.kind === 'check' && l.text.trim() !== '');
  return { done: checks.filter((l) => l.checked).length, total: checks.length };
}

/** Lines in display order, applying the note's completed-item behaviour. */
export function visibleLines(note: Pick<Note, 'lines' | 'style'>, editing: boolean): Line[] {
  const mode = note.style.completed;
  if (mode === 'strike' || editing) return note.lines;
  if (mode === 'hide') return note.lines.filter((l) => !(l.kind === 'check' && l.checked));
  const open = note.lines.filter((l) => !(l.kind === 'check' && l.checked));
  const done = note.lines.filter((l) => l.kind === 'check' && l.checked);
  return [...open, ...done];
}

export function duplicateNote(note: Note): Note {
  const now = Date.now();
  return {
    ...structuredClone(note),
    id: uid('n'),
    title: note.title ? `${note.title} (copy)` : '',
    lines: note.lines.map((l) => ({ ...l, id: uid('l') })),
    images: note.images.map((i) => ({ ...i, id: uid('i') })),
    strokes: note.strokes.map((s) => ({ ...s, id: uid('s') })),
    pinned: false,
    deletedAt: null,
    archived: false,
    createdAt: now,
    updatedAt: now,
    order: -now,
    rev: 0,
  };
}

/** Line operations used by the editor. All return new arrays. */
export const lineOps = {
  update(lines: Line[], id: string, patch: Partial<Line>): Line[] {
    return lines.map((l) => (l.id === id ? { ...l, ...patch } : l));
  },
  splitAt(lines: Line[], id: string, caret: number): { lines: Line[]; focusId: string } {
    const i = lines.findIndex((l) => l.id === id);
    if (i < 0) return { lines, focusId: id };
    const cur = lines[i];
    const next = newLine(cur.text.slice(caret), cur.kind);
    next.marks = cur.marks;
    const copy = [...lines];
    copy.splice(i, 1, { ...cur, text: cur.text.slice(0, caret) }, next);
    return { lines: copy, focusId: next.id };
  },
  mergeWithPrevious(lines: Line[], id: string): { lines: Line[]; focusId: string; caret: number } | null {
    const i = lines.findIndex((l) => l.id === id);
    if (i <= 0) return null;
    const prev = lines[i - 1];
    const cur = lines[i];
    const copy = [...lines];
    copy.splice(i - 1, 2, { ...prev, text: prev.text + cur.text });
    return { lines: copy, focusId: prev.id, caret: prev.text.length };
  },
  move(lines: Line[], id: string, delta: number): Line[] {
    const i = lines.findIndex((l) => l.id === id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= lines.length) return lines;
    const copy = [...lines];
    const [item] = copy.splice(i, 1);
    copy.splice(j, 0, item);
    return copy;
  },
  moveTo(lines: Line[], id: string, index: number): Line[] {
    const i = lines.findIndex((l) => l.id === id);
    if (i < 0) return lines;
    const copy = [...lines];
    const [item] = copy.splice(i, 1);
    copy.splice(Math.max(0, Math.min(copy.length, index)), 0, item);
    return copy;
  },
  remove(lines: Line[], id: string): Line[] {
    const rest = lines.filter((l) => l.id !== id);
    return rest.length ? rest : [newLine()];
  },
  toggleChecklist(lines: Line[], ids: string[]): Line[] {
    const target = ids.length ? lines.filter((l) => ids.includes(l.id)) : lines;
    const makeCheck = target.some((l) => l.kind !== 'check');
    const set = new Set(target.map((l) => l.id));
    return lines.map((l) => (set.has(l.id) ? { ...l, kind: makeCheck ? 'check' : 'text', checked: makeCheck ? l.checked : false } : l));
  },
  /** Paste of multi-line text: first fragment joins the current line, the rest become new lines. */
  insertText(lines: Line[], id: string, caret: number, text: string): { lines: Line[]; focusId: string; caret: number } {
    const parts = text.replace(/\r\n?/g, '\n').split('\n');
    const i = lines.findIndex((l) => l.id === id);
    if (i < 0) return { lines, focusId: id, caret };
    const cur = lines[i];
    const before = cur.text.slice(0, caret);
    const after = cur.text.slice(caret);
    if (parts.length === 1) {
      const t = before + parts[0] + after;
      return { lines: lineOps.update(lines, id, { text: t }), focusId: id, caret: caret + parts[0].length };
    }
    const created: Line[] = parts.slice(1).map((p) => {
      const m = /^\s*(?:[-*•]\s*)?\[( |x|X)\]\s?(.*)$/.exec(p);
      if (m) return { ...newLine(m[2], 'check'), checked: m[1].toLowerCase() === 'x' };
      return newLine(p, cur.kind);
    });
    const last = created[created.length - 1];
    const caretOut = last.text.length;
    last.text += after;
    const copy = [...lines];
    copy.splice(i, 1, { ...cur, text: before + parts[0] }, ...created);
    return { lines: copy, focusId: last.id, caret: caretOut };
  },
};
